import { useState, useCallback } from 'react';
import {
	Box,
	Button,
	H3,
	IconCopy,
	Input,
	Select,
	SelectOptionType,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { productMap } from 'domain/product/productMap';
import { styled } from 'styled-components';

interface FormState {
	applicationId: string;
	cta: string;
	origin: string;
}

const initialState: FormState = {
	applicationId: '',
	cta: '',
	origin: '',
};

const applicationOptions: SelectOptionType[] = productMap
	.filter((p) => !!p.id)
	.map((p) => ({ label: p.name, value: p.id }));

const TrackingStateGenerator = () => {
	const [form, setForm] = useState<FormState>(initialState);
	const [result, setResult] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const onCopy = useCallback(() => {
		if (!result) return;
		navigator.clipboard.writeText(result).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	}, [result]);

	const canSubmit = !!form.applicationId && !!form.cta && !!form.origin;

	const onSubmit = () => {
		const payload = {
			applicationId: form.applicationId,
			returnUrl: '/',
			tracking: {
				source: form.origin,
				cta: form.cta,
			},
		};

		const json = JSON.stringify(payload);
		const encoded = btoa(json);
		setResult(encoded);
	};

	const selectedApplication =
		applicationOptions.find((o) => o.value === form.applicationId) ?? null;

	return (
		<Box bg="bgMainContainer">
			<FormContainer bg="bgMainContainer">
				<VStack gap="md">
					<H3>SSO Form Tracking State Generator</H3>
					<Select
						label="Application"
						options={applicationOptions}
						required
						value={selectedApplication}
						onChange={(selected: SelectOptionType) =>
							setForm({ ...form, applicationId: selected.value })
						}
					/>
					<Input
						label="CTA"
						type="text"
						name="cta"
						required
						value={form.cta}
						onChange={(e) =>
							setForm({ ...form, cta: e.target.value })
						}
					/>
					<Input
						label="Registration source"
						type="text"
						name="origin"
						required
						value={form.origin}
						onChange={(e) =>
							setForm({ ...form, origin: e.target.value })
						}
					/>
					<Button
						text="Generate"
						isDisabled={!canSubmit}
						onClick={onSubmit}
					/>
					{result !== null && (
						<VStack gap="xs" fullWidth>
							<ResultHeader>
								<ResultLabel>Base64 output</ResultLabel>
								<Button
									leftIcon={<IconCopy />}
									text={copied ? 'Copied!' : 'Copy'}
									variant="tertiary"
									size="sm"
									onClick={onCopy}
								/>
							</ResultHeader>
							<ResultBox>{result}</ResultBox>
						</VStack>
					)}
				</VStack>
			</FormContainer>
		</Box>
	);
};

const FormContainer = styled(Box)`
	max-width: 600px;
`;

const ResultLabel = styled.span`
	font-weight: 600;
	font-size: 0.875rem;
`;

const ResultHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

const ResultBox = styled.pre`
	width: 100%;
	padding: 12px;
	background: #f4f4f4;
	color: #1a1a1a;
	border: 1px solid #ddd;
	border-radius: 4px;
	word-break: break-all;
	white-space: pre-wrap;
	font-size: 0.8rem;
	user-select: all;
`;

export default TrackingStateGenerator;
